import { memo, useMemo } from 'react';
import { DependencyConnector } from '@/components/atoms';
import type { DependencyConnection } from '@/types/timeline';

export interface DependencyOverlayProps {
  /** Active connections to render */
  connections: DependencyConnection[];
  /** Width of the SVG overlay */
  width: number;
  /** Height of the SVG overlay */
  height: number;
  /** ID of the deliverable whose connections are currently shown */
  selectedConnectionId: string | null;
}

/**
 * DependencyOverlay molecule - SVG layer rendering dependency connection lines
 *
 * Renders bezier curves connecting related deliverables when one is hovered.
 * The overlay sits on top of the timeline chart and shows:
 * - Incoming connections (predecessors) in blue
 * - Outgoing connections (dependents) in orange
 *
 * Architecture:
 * - Rendered as a separate SVG `<g>` element layered over the chart
 * - Uses pointer-events: none to not interfere with row interactions
 * - Connections are computed by FeatureTimeline and passed as props
 */
export const DependencyOverlay = memo(function DependencyOverlay({
  connections,
  width,
  height,
  selectedConnectionId,
}: DependencyOverlayProps) {
  // Sort connections: non-highlighted first, highlighted last (so they render on top)
  const sortedConnections = useMemo(() => {
    return [...connections].sort((a, b) => {
      const aHighlighted = a.fromId === selectedConnectionId || a.toId === selectedConnectionId;
      const bHighlighted = b.fromId === selectedConnectionId || b.toId === selectedConnectionId;
      return aHighlighted === bHighlighted ? 0 : aHighlighted ? 1 : -1;
    });
  }, [connections, selectedConnectionId]);

  if (connections.length === 0) {
    return null;
  }

  return (
    <g className="dependency-overlay" style={{ pointerEvents: 'none' }}>
      {/* Background fade effect when connections are shown */}
      {selectedConnectionId && (
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill="var(--color-bg-base)"
          fillOpacity={0.3}
          className="transition-opacity duration-200"
        />
      )}

      {/* Render all connections */}
      {sortedConnections.map((connection) => {
        const isHighlighted = connection.fromId === selectedConnectionId || connection.toId === selectedConnectionId;

        return (
          <DependencyConnector
            key={`${connection.fromId}-${connection.toId}`}
            from={connection.from}
            to={connection.to}
            type={connection.type}
            isHighlighted={isHighlighted}
            isCollapsed={connection.isCollapsed}
            direction={connection.direction}
            opacity={connection.opacity}
          />
        );
      })}
    </g>
  );
});
